import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../services/api';
import {
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Badge,
  InputAdornment,
  Tab,
  Tabs,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  CircularProgress
  // Assignment as AssignmentIcon
} from '@mui/material';
import {
  Search,
  Send,
  AttachFile,
  EmojiEmotions,
  MoreVert,
  Label,
  Person,
  Schedule,
  CheckCircle,
  RadioButtonUnchecked,
  KeyboardArrowDown,
  ChevronLeft,
  ChevronRight,
  Sort,
  FilterList,
  Group,
  Analytics,
  ExpandLess,
  ExpandMore,
  ContactPage,
  Psychology,
  Assignment as AssignmentIcon,
  Download
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { conversationsAPI, messagesAPI, captainAIAPI } from '../services/api';
import { isBotHandoverMessage } from '../utils/handoverDetection';
import AssignmentDialog from '../components/conversations/AssignmentDialog';
import LabelsDialog from '../components/conversations/LabelsDialog';
import RichTextEditor from '../components/conversations/RichTextEditor';
import VoiceRecorder from '../components/conversations/VoiceRecorder';
import SpeechToTextDialog from '../components/conversations/SpeechToTextDialog';
import SignatureDialog from '../components/conversations/SignatureDialog';
import ParticipantsDialog from '../components/conversations/ParticipantsDialog';
import ConversationMetrics from '../components/conversations/ConversationMetrics';
import CoPilotPanel from '../components/CoPilot/CoPilotPanel';
import CoPilotToggle from '../components/CoPilot/CoPilotToggle';
import { formatMessageTime, formatConversationTime } from '../utils/dateUtils'; // 08/02/2026 - Added for timezone implementation
import {
  buildExportRows,
  exportConversations,
  type ExportFormat,
  type ExportConversation,
  type ExportMessage
} from '../utils/conversationExport'; // added on 18-02-2026 for exports 
import {
  AIStatusIndicator,
  AIConversationControls,
  EscalationBanner,
  AIMessageBubble
} from '../components/AI';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { usePermission } from '../hooks/usePermission'; // 14/11/2025 - User Access Management Module Phase 5.3.1
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchUserPermissions } from '../store/slices/authSlice';
// Phase 5: WhatsApp Session Management UI (2026-01-19)
import {
  TemplateSuggestor,
  QuickTemplateSelector,
  ConversationInitiator
} from '../components/conversations';
import EmailTemplateSuggestor from '../components/conversations/EmailTemplateSuggestor'; // 09/02/2026 - Added for email template suggestor

interface Conversation {
  id: number;
  display_id: number;
  created_at: string;
  contact: {
    id: number;
	name?: string | null;
    // name: string;
    email?: string;
    phone_number?: string;
    avatar_url?: string;
    custom_attributes?: Record<string, any>;
  // };
  } | null;
  status: 'open' | 'pending' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  last_activity_at: string;
  ai_enabled: boolean;
  assignee?: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar_url?: string;
  };
  unread_count: number;
  labels: Array<{
    id: number;
    title: string;
    color: string;
    description?: string;
  }>;
  channel: {
    id: number;
    name: string;
    channel_type: string;
  };
  escalated?: boolean;
  escalation_reason?: string;
  escalation_trigger?: string;
  escalated_at?: string;
  escalated_by?: string;
}

/** Label for list/search when API allows null contact.name (falls back to phone/email). */
function contactDisplayLabel(
  contact: Conversation['contact'] | null | undefined
): string {
  if (!contact) return '';
  const trimmed = contact.name?.trim();
  if (trimmed) return trimmed;
  return contact.phone_number?.trim() || contact.email?.trim() || '';
}

interface Attachment {
  id: number;
  file_type: string;
  file_url: string;
  thumb_url?: string;
  file_size?: number;
  direction: string;
  whatsapp_media_id?: string;
  mime_type?: string;
}

interface Message {
  id: number;
  content: string;
  message_type: 'incoming' | 'outgoing' | 'activity';
  content_type?: 'text' | 'file' | 'image' | 'audio' | 'video';
  sender?: {
    name: string;
    avatar_url?: string;
  };
  sender_type?: 'user' | 'agent' | 'ai' | 'bot';
  created_at: string;
  private: boolean;
  content_attributes?: {
    filename?: string;
    file_size?: number;
    file_type?: string;
    file_url?: string;
    mime_type?: string;
  };
  attachments?: Attachment[];  // New: Attachment records from database
  ai_confidence?: number;
  tools_used?: string[];
  response_time_ms?: number;
  streaming?: boolean;
  metadata?: {  // ADDED: 27/01/2026 by NK
    whatsapp?: any;
    button?: {
      payload?: string;
      text?: string;
      context?: any;
      type?: string;
    };
    interactive?: any;
  };
}

const ConversationsPage: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { user, permissionsLoaded } = useAppSelector(state => state.auth);

  // 14/11/2025 - User Access Management Module Phase 5.3.1
  // Determine conversation scope based on permissions
  const hasViewAll = usePermission('conversations:view_all');
  const hasViewTeam = usePermission('conversations:view_team');
  const hasViewAssigned = usePermission('conversations:view_assigned');
  const canAssign = usePermission('conversations:assign');

  const [selectedTab, setSelectedTab] = useState(0);
  const [tabInitialized, setTabInitialized] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState('');
  // Add these state variables near other useState declarations (around line 200-250) added on 27/01/2026
  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: number;
    name: string;
    variables: Record<string, string>;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false); // conversation list loading only
  const [error, setError] = useState<string | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState('last_activity_at');
  const [uploading, setUploading] = useState(false);
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);
  const [speechToTextDialogOpen, setSpeechToTextDialogOpen] = useState(false);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [userSignature, setUserSignature] = useState('');
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [metricsDialogOpen, setMetricsDialogOpen] = useState(false);
  const [showPreChatData, setShowPreChatData] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const [conversationCounts, setConversationCounts] = useState({
    mine: 0,
    unassigned: 0,
    all: 0,
    team: 0  // 14/11/2025 - Added for team conversations count
  });
  const [lastProcessedMessageId, setLastProcessedMessageId] = useState<number>(0);

  // Co-Pilot state
  const [copilotEnabled, setCopilotEnabled] = useState(false);

  // Phase 5: WhatsApp Session Management UI (2026-01-19)
  const [conversationInitiatorOpen, setConversationInitiatorOpen] = useState(false);
  const [sessionState, setSessionState] = useState<'ACTIVE_24H' | 'TEMPLATE_ONLY' | 'NO_RESPONSE' | 'CLOSED'>('TEMPLATE_ONLY');
  const [templateSuggestorRefreshKey, setTemplateSuggestorRefreshKey] = useState(0);

  // Pagination state for messages
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Ref for auto-scrolling to latest message
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const previousMessageCountRef = useRef(0);
  const firstMessageIdRef = useRef<number | null>(null);

  // 14/11/2025 - User Access Management Module Phase 5.3.1
  // Build available tabs based on user permissions
  const availableTabs = React.useMemo(() => {
    const tabs: Array<{ label: string; value: string; scope: string }> = [];

    // Assigned tab - always available if user has any view permission
    if (hasViewAssigned || hasViewTeam || hasViewAll) {
      tabs.push({
        label: `Mine (${conversationCounts.mine})`,
        value: 'assigned',
        scope: 'assigned'
      });
    }

    // Team tab - available if user can view team conversations
    if (hasViewTeam || hasViewAll) {
      tabs.push({
        label: `Team (${conversationCounts.team})`,
        value: 'team',
        scope: 'team'
      });
    }

    // All tab - available only if user can view all conversations
    if (hasViewAll) {
      tabs.push({
        label: `All (${conversationCounts.all})`,
        value: 'all',
        scope: 'all'
      });
    }

    // Unassigned tab - available if user can assign conversations
    if (canAssign) {
      tabs.push({
        label: `Unassigned (${conversationCounts.unassigned})`,
        value: 'unassigned',
        scope: 'unassigned'
      });
    }

    return tabs;
  }, [hasViewAll, hasViewTeam, hasViewAssigned, canAssign, conversationCounts]);

  // Ensure permissions are loaded before scoping conversation tabs
  useEffect(() => {
    if (!permissionsLoaded) {
      dispatch(fetchUserPermissions());
    }
  }, [dispatch, permissionsLoaded]);

  // Default to "All" for users who can view every conversation (WhatsApp often unassigned)
  useEffect(() => {
    if (tabInitialized || availableTabs.length === 0) {
      return;
    }
    const allTabIndex = availableTabs.findIndex((tab) => tab.scope === 'all');
    if (hasViewAll && allTabIndex >= 0) {
      setSelectedTab(allTabIndex);
    }
    setTabInitialized(true);
  }, [availableTabs, hasViewAll, tabInitialized]);

  // v1 auto-scroll: Only scroll when messages added to END, not START (older messages)
  useEffect(() => {
    const currentCount = messages.length;
    const previousCount = previousMessageCountRef.current;
    const firstMessageId = messages.length > 0 ? messages[0].id : null;
    const previousFirstMessageId = firstMessageIdRef.current;

    // Only auto-scroll if:
    // 1. Messages array grew (new messages added)
    // 2. AND the first message ID didn't change (means messages were added to END, not START)
    // 3. AND we're not currently loading older messages
    const messagesAddedToEnd = currentCount > previousCount &&
                               firstMessageId === previousFirstMessageId &&
                               !loadingOlderMessages;

    if (messagesAddedToEnd && shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    previousMessageCountRef.current = currentCount;
    firstMessageIdRef.current = firstMessageId;
  }, [messages, loadingOlderMessages, shouldAutoScroll]);

  // v1 scroll handler: Detect scroll to top for infinite scroll + track bottom position
  const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;

    // Track if user is at bottom for auto-scroll
    const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    setShouldAutoScroll(isAtBottom);

    // Check if scrolled to top (within 50px threshold) for infinite scroll
    if (element.scrollTop < 50 && hasMoreMessages && !loadingOlderMessages) {
      const previousScrollHeight = element.scrollHeight;
      const previousScrollTop = element.scrollTop;

      handleLoadOlderMessages().then(() => {
        // Maintain scroll position after loading older messages
        if (messagesContainerRef.current) {
          const newScrollHeight = messagesContainerRef.current.scrollHeight;
          messagesContainerRef.current.scrollTop = previousScrollTop + (newScrollHeight - previousScrollHeight);
        }
      });
    }
  };

  // Load user signature settings
  useEffect(() => {
    const savedSignature = localStorage.getItem('userSignature') || '';
    const savedSignatureEnabled = localStorage.getItem('signatureEnabled') === 'true';
    setUserSignature(savedSignature);
    setSignatureEnabled(savedSignatureEnabled);
  }, []);

  // Load participants when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      const loadParticipants = async () => {
        try {
          const response = await conversationsAPI.getParticipants(selectedConversation.id);
          setParticipants(response.participants || []);
        } catch (err) {
          console.error('Failed to load participants:', err);
          setParticipants([]);
        }
      };
      loadParticipants();
    }
  }, [selectedConversation]);

  // Load conversations from API
  const loadConversations = async () => {
      try {
        setLoading(true);
        setError(null);

        // const params: any = {};
		const params: any = { per_page: 100 }; // 

        // Apply status filter
        if (filterStatus !== 'all') {
          params.status = filterStatus;
        }

        // 14/11/2025 - User Access Management Module Phase 5.3.1
        // Apply tab-based filtering using permission-based scoping
        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') {
          params.scope = currentTab.scope; // 'assigned', 'team', or 'unassigned'
        } else if (!currentTab) {
          const role = (user?.role || '').toLowerCase().replace(/-/g, '_');
          if (role !== 'super_admin' && role !== 'admin') {
            params.scope = 'assigned';
          }
        }
        // If scope is 'all' or admin with no tab, no additional filtering needed

        const response = await conversationsAPI.getConversations(params);
        setConversations(response.conversations || []);

        // Update conversation counts using response.total (not .length) so counts match backend totals and stay in sync with sidebar. -- 18/02/2026
        // Use same status filter as list so tab counts align with left-panel total when status filter is applied.
        const countParams: any = { per_page: 1 };
        if (filterStatus !== 'all') {
          countParams.status = filterStatus;
        }

        const [allResponse, mineResponse, teamResponse, unassignedResponse] = await Promise.all([
          conversationsAPI.getConversations({ ...countParams }),
          conversationsAPI.getConversations({ ...countParams, scope: 'assigned' }),
          (hasViewTeam || hasViewAll)
            ? conversationsAPI.getConversations({ ...countParams, scope: 'team' })
            : Promise.resolve({ total: 0 }),
          canAssign
            ? conversationsAPI.getConversations({ ...countParams, scope: 'unassigned' })
            : Promise.resolve({ total: 0 })
        ]);

        setConversationCounts({
          mine: mineResponse.total ?? 0,
          unassigned: unassignedResponse.total ?? 0,
          all: allResponse.total ?? 0,
          team: teamResponse.total ?? 0
        }); //-- 18/02/2026
		
        // If there's an ID in the URL, find and set the selected conversation
        if (id && response.conversations) {
          const conv = response.conversations.find((c: Conversation) => c.id === parseInt(id));
          setSelectedConversation(conv || null);
        }
      } catch (err) {
        console.error('Failed to load conversations:', err);
        setError('Failed to load conversations');
        setConversations([]);
      } finally {
        setLoading(false);
      }
  };

  // Load conversations on mount and when filters change
  useEffect(() => {
    // Load conversations immediately
    loadConversations();

    // Poll for conversations every 5 seconds to keep list updated
    const pollInterval = setInterval(async () => {
      try {
        // const params: any = {};
        const params: any = { per_page: 100 };
		if (filterStatus !== 'all') {
          params.status = filterStatus;
        }

        // 18/02/2026 - User Access Management Module Phase 5.3.1
        // Apply tab-based filtering using permission-based scoping in polling interval
        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') {
          params.scope = currentTab.scope;
        } else if (!currentTab) {
          const role = (user?.role || '').toLowerCase().replace(/-/g, '_');
          if (role !== 'super_admin' && role !== 'admin') {
            params.scope = 'assigned';
          }
        }

        const response = await conversationsAPI.getConversations(params);
        const polled = response.conversations || [];
        setConversations(polled);

        // Keep selected conversation handoff fields in sync with server
        setSelectedConversation(prev => {
          if (!prev) return prev;
          const fresh = polled.find((c: Conversation) => c.id === prev.id);
          return fresh ? { ...prev, ...fresh } : prev;
        });

        // When still showing AI mode, re-fetch handoff state (bot may have escalated)
        const current = selectedConversationRef.current;
        if (current?.id && current.ai_enabled) {
          conversationsAPI.getConversation(current.id).then((fresh) => {
            if (!fresh.ai_enabled || fresh.escalated) {
              setSelectedConversation((prev) =>
                prev && prev.id === current.id ? { ...prev, ...fresh } : prev
              );
              setConversations((prev) =>
                prev.map((conv) => (conv.id === current.id ? { ...conv, ...fresh } : conv))
              );
            }
          }).catch(() => {});
        }

        // 14/11/2025 - User Access Management Module Phase 5.3.1
        // Update conversation counts using response.total so counts stay in sync and update on poll.
        const countParams: any = { per_page: 1 };
        if (filterStatus !== 'all') {
          countParams.status = filterStatus;
        }

        const [allResponse, mineResponse, teamResponse, unassignedResponse] = await Promise.all([
          conversationsAPI.getConversations({ ...countParams }),
          conversationsAPI.getConversations({ ...countParams, scope: 'assigned' }),
          (hasViewTeam || hasViewAll)
            ? conversationsAPI.getConversations({ ...countParams, scope: 'team' })
            : Promise.resolve({ total: 0 }),
          canAssign
            ? conversationsAPI.getConversations({ ...countParams, scope: 'unassigned' })
            : Promise.resolve({ total: 0 })
        ]);

        setConversationCounts({
          mine: mineResponse.total ?? 0,
          unassigned: unassignedResponse.total ?? 0,
          all: allResponse.total ?? 0,
          team: teamResponse.total ?? 0
        });
      } catch (err) {
        // Silently fail for polling errors
        console.error('Polling error:', err);
      }
    }, 5000);

    // Cleanup interval
    return () => clearInterval(pollInterval);
  }, [id, filterStatus, selectedTab, availableTabs, hasViewAll, hasViewTeam, canAssign, user?.role, permissionsLoaded]);

  // Extract loadMessages function to be reusable
  const loadMessages = useCallback(async () => {
    if (!selectedConversation) return;
    
    try {
      // First, get total count
      const initialResponse = await messagesAPI.getMessages(selectedConversation.id, {
        page: 1,
        per_page: 1
      });
      const total = initialResponse.total || 0;
      setTotalMessages(total);

      // If total <= 50, just load everything from page 1
      if (total <= 50) {
        const response = await messagesAPI.getMessages(selectedConversation.id, {
          page: 1,
          per_page: 50
        });
        const newMessages = response.messages || [];

        setCurrentPage(1);
        setHasMoreMessages(false);
        setMessages(newMessages);
        return;
      }

      // For total > 50: Load latest 50 messages
      const messagesPerPage = 50;
      const toLoad = 50;
      const skip = total - toLoad;

      // Calculate which pages we need
      const startPage = Math.floor(skip / messagesPerPage) + 1;
      const lastPage = Math.ceil(total / messagesPerPage);

      // Fetch all pages from startPage to lastPage
      const allMessages = [];
      for (let page = startPage; page <= lastPage; page++) {
        const response = await messagesAPI.getMessages(selectedConversation.id, {
          page,
          per_page: 50
        });
        allMessages.push(...(response.messages || []));
      }

      // Slice to get exactly the latest 50 messages
      const skipWithinFirstPage = skip % messagesPerPage;
      const finalMessages = allMessages.slice(skipWithinFirstPage, skipWithinFirstPage + toLoad);

      setCurrentPage(startPage);
      setHasMoreMessages(skip > 0);
      setMessages(finalMessages);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setMessages([]);
    }
  }, [selectedConversation?.id]);

  // Track conversation id so handoff/metadata updates don't wipe the composer
  const selectedConversationIdRef = useRef<number | null>(null);
  const selectedConversationRef = useRef<Conversation | null>(null);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Load messages for selected conversation with v1 smart pagination
  useEffect(() => {
    if (selectedConversation) {
      const conversationChanged =
        selectedConversationIdRef.current !== selectedConversation.id;
      selectedConversationIdRef.current = selectedConversation.id;

      // Only reset composer when switching to a different conversation
      if (conversationChanged) {
        setSelectedTemplate(null);
        setMessageText('');
        setMessages([]);
        setTotalMessages(0);
        setHasMoreMessages(false);
      }

      /*
      const loadMessages = async () => {
        try {
          // First, get total count
          const initialResponse = await messagesAPI.getMessages(selectedConversation.id, {
            page: 1,
            per_page: 1
          });
          const total = initialResponse.total || 0;
          setTotalMessages(total);

          // If total <= 50, just load everything from page 1
          if (total <= 50) {
            const response = await messagesAPI.getMessages(selectedConversation.id, {
              page: 1,
              per_page: 50
            });
            const newMessages = response.messages || [];

            setCurrentPage(1);
            setHasMoreMessages(false);
            setMessages(newMessages);
            return;
          }

          // For total > 50: Load latest 50 messages
          const messagesPerPage = 50;
          const toLoad = 50;
          const skip = total - toLoad;

          // Calculate which pages we need
          const startPage = Math.floor(skip / messagesPerPage) + 1;
          const lastPage = Math.ceil(total / messagesPerPage);

          // Fetch all pages from startPage to lastPage
          const allMessages = [];
          for (let page = startPage; page <= lastPage; page++) {
            const response = await messagesAPI.getMessages(selectedConversation.id, {
              page,
              per_page: 50
            });
            allMessages.push(...(response.messages || []));
          }

          // Slice to get exactly the latest 50 messages
          const skipWithinFirstPage = skip % messagesPerPage;
          const finalMessages = allMessages.slice(skipWithinFirstPage, skipWithinFirstPage + toLoad);

          setCurrentPage(startPage);
          setHasMoreMessages(skip > 0);
          setMessages(finalMessages);
        } catch (err) {
          console.error('Failed to load messages:', err);
          setMessages([]);
        }
      };
      */

      // Load messages immediately
      loadMessages(); 

      // Poll for new messages every 3 seconds
      const pollInterval = setInterval(loadMessages, 3000);

      // Cleanup interval on unmount or conversation change
      return () => clearInterval(pollInterval);
    }
  }, [selectedConversation?.id, lastProcessedMessageId, loadMessages]);

/*  // commented out on 27/01/2026
  const handleSendMessage = async () => {    
    if (messageText.trim() && selectedConversation) {
      try {
        // Add signature if enabled
        let messageContent = messageText;
        if (signatureEnabled && userSignature.trim()) {
          messageContent = messageText + '\n\n' + userSignature;
        }

        const response = await messagesAPI.sendMessage(selectedConversation.id, {
          content: messageContent,
          message_type: 'outgoing',
          private: false
        });

        // Add the new message to the list (v1 approach)
        setMessages(prevMessages => [...prevMessages, response]);
        setMessageText('');

        // Update conversation's last activity
        setConversations(prevConversations =>
          prevConversations.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, last_activity_at: new Date().toISOString() }
              : conv
          )
        );
        setTemplateSuggestorRefreshKey(k => k + 1);
      } catch (err) {
        console.error('Failed to send message:', err);
        setError('Failed to send message');
      }
    }
  };
*/
// added on 27/01/2026
  const handleSendMessage = async () => {
    if (!selectedConversation) return;
    
    // Check if a template is selected
    if (selectedTemplate) {
      await sendSelectedTemplate();
      return;
    }
    
    // Regular text message (existing logic)
    if (messageText.trim()) {
      try {
        // Add signature if enabled
        let messageContent = messageText;
        if (signatureEnabled && userSignature.trim()) {
          messageContent = messageText + '\n\n' + userSignature;
        }

        const response = await messagesAPI.sendMessage(selectedConversation.id, {
          content: messageContent,
          message_type: 'outgoing',
          private: false
        });

        // Add the new message to the list (v1 approach)
        setMessages(prevMessages => [...prevMessages, response]);
        setMessageText('');

        // Update conversation's last activity
        setConversations(prevConversations =>
          prevConversations.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, last_activity_at: new Date().toISOString() }
              : conv
          )
        );
        setTemplateSuggestorRefreshKey(k => k + 1);
      } catch (err) {
        console.error('Failed to send message:', err);
        setError('Failed to send message');
      }
    }
  };
// added on 27/01/2026
  const handleMessageTextChange = (text: string) => {
    setMessageText(text);
    // If user manually edits, clear template selection
    if (selectedTemplate && text !== messageText) {
      setSelectedTemplate(null);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedConversation) return;
    
    try {
      await conversationsAPI.toggleStatus(selectedConversation.id, status);
      
      // Update conversation status in local state
      setSelectedConversation(prev => prev ? { ...prev, status: status as any } : null);
      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.id === selectedConversation.id
            ? { ...conv, status: status as any }
            : conv
        )
      );
      
      setAnchorEl(null);
    } catch (err) {
      console.error('Failed to change status:', err);
      setError('Failed to change conversation status');
    }
  };

  const handleAssignmentComplete = () => {
    // Reload conversations to get updated assignment info (same params as main list, including per_page) -- 18/02/2026
    const reloadConversations = async () => {
      try {
        const params: any = { per_page: 100 };
        if (filterStatus !== 'all') params.status = filterStatus;
        const currentTab = availableTabs[selectedTab];
        if (currentTab && currentTab.scope !== 'all') params.scope = currentTab.scope;
        
        const response = await conversationsAPI.getConversations(params);
        setConversations(response.conversations || []);
        
        // Update selected conversation
        if (selectedConversation && response.conversations) {
          const updatedConv = response.conversations.find((c: Conversation) => c.id === selectedConversation.id);
          // if (updatedConv) {
          //   setSelectedConversation(updatedConv);
          // }
		  if (updatedConv) setSelectedConversation(updatedConv);
        }
      } catch (err) {
        console.error('Failed to reload conversations:', err);
      }
    };
    
    // loadConversations();
	reloadConversations();
  };

  const handleLabelsUpdated = () => {
    handleAssignmentComplete(); // Reuse the same refresh logic
  };

  // Fetch all messages for a conversation (paginate until a short/empty page — works even if `total` is missing)
  const fetchAllMessagesForConversation = useCallback(async (conversationId: number): Promise<Message[]> => {
    const perPage = 100;
    const allMessages: Message[] = [];
    for (let page = 1; ; page++) {
      const res = await messagesAPI.getMessages(conversationId, { page, per_page: perPage });
      const batch = res.messages || [];
      allMessages.push(...batch);
      if (batch.length < perPage) break;
    }
    return allMessages;
  }, []);

  // Fetch all conversations for export (same filters as current view; page until exhausted — not only `total`)
  const fetchAllConversationsForExport = useCallback(async (): Promise<Conversation[]> => {
    const perPage = 100;
    const base: any = { per_page: perPage };
    if (filterStatus !== 'all') base.status = filterStatus;
    const currentTab = availableTabs[selectedTab];
    if (currentTab && currentTab.scope !== 'all') base.scope = currentTab.scope;
    const all: Conversation[] = [];
    for (let page = 1; ; page++) {
      const res = await conversationsAPI.getConversations({ ...base, page });
      const batch = res.conversations || [];
      all.push(...batch);
      if (batch.length < perPage) break;
    }
    return all;
  }, [filterStatus, selectedTab, availableTabs]);

  // Match backend + shadcn: contact/channel can be null on list rows; never throw or export aborts mid-loop.
  const toExportConv = (c: Conversation): ExportConversation => ({
    id: c.id,
    display_id: c.display_id,
    status: c.status,
    contact: {
      id: c.contact?.id ?? 0,
      name: c.contact?.name ?? '',
      email: c.contact?.email,
      phone_number: c.contact?.phone_number
    },
    assignee: c.assignee ? { id: c.assignee.id, full_name: c.assignee.full_name } : undefined,
    channel: c.channel ? { name: c.channel.name, channel_type: c.channel.channel_type } : undefined,
    created_at: c.created_at,
    last_activity_at: c.last_activity_at
  });

  const toExportMsg = (m: Message): ExportMessage => ({
    id: m.id,
    content: m.content,
    message_type: m.message_type,
    content_type: m.content_type,
    sender: m.sender,
    sender_type: m.sender_type,
    created_at: m.created_at,
    private: m.private
  });

  const handleDownloadCurrent = useCallback(async (format: ExportFormat) => {
    if (!selectedConversation) return;
    setDownloadMenuAnchor(null);
    setExporting(true);
    try {
      const msgs = await fetchAllMessagesForConversation(selectedConversation.id);
      const rows = buildExportRows([{
        conversation: toExportConv(selectedConversation),
        messages: msgs.map(toExportMsg)
      }]);
      const name = `conversation-${selectedConversation.display_id}-${selectedConversation.contact?.name?.replace(/[^a-zA-Z0-9]/g, '_') || selectedConversation.id}`;
      exportConversations(rows, name, format);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [selectedConversation, fetchAllMessagesForConversation]);

  const handleDownloadAll = useCallback(async (format: ExportFormat) => {
    setDownloadMenuAnchor(null);
    setExporting(true);
    try {

      const allConversations = await fetchAllConversationsForExport();
	  
      const payloads: Array<{ conversation: ExportConversation; messages: ExportMessage[] }> = [];
      for (const conv of allConversations) {
        const msgs = await fetchAllMessagesForConversation(conv.id);
        payloads.push({ conversation: toExportConv(conv), messages: msgs.map(toExportMsg) });
      }
      const rows = buildExportRows(payloads);
      const name = `conversations-export-${new Date().toISOString().slice(0, 10)}`;
      exportConversations(rows, name, format);
    } catch (err) {
      console.error('Export all failed:', err);
    } finally {
      setExporting(false);
    }
//  }, [conversations, fetchAllMessagesForConversation]);
    }, [fetchAllConversationsForExport, fetchAllMessagesForConversation]);


  const handleFileAttachment = async () => {
    if (!selectedConversation) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.accept = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls';
    
    input.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (!file) return;
      
      try {
        setUploading(true);
        const response = await messagesAPI.uploadAttachment(selectedConversation.id, file, false);

        // Add the new message to the list
        setMessages(prevMessages => [...prevMessages, response]);

        // Update conversation's last activity
        setConversations(prevConversations =>
          prevConversations.map(conv =>
            conv.id === selectedConversation.id
              ? { ...conv, last_activity_at: new Date().toISOString() }
              : conv
          )
        );
      } catch (err) {
        console.error('Failed to upload attachment:', err);
        setError('Failed to upload file');
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  const handleVoiceRecording = async (audioBlob: Blob) => {
    if (!selectedConversation) return;

    try {
      setUploading(true);

      // Convert blob to file
      const audioFile = new File([audioBlob], 'voice-message.webm', { type: 'audio/webm' });

      const response = await messagesAPI.uploadAttachment(selectedConversation.id, audioFile, false);

      // Add the new message to the list
      setMessages(prevMessages => [...prevMessages, response]);

      // Update conversation's last activity
      setConversations(prevConversations =>
        prevConversations.map(conv =>
          conv.id === selectedConversation.id
            ? { ...conv, last_activity_at: new Date().toISOString() }
            : conv
        )
      );
    } catch (err) {
      console.error('Failed to send voice message:', err);
      setError('Failed to send voice message');
    } finally {
      setUploading(false);
    }
  };

  const handleSpeechToTextComplete = (transcript: string) => {
    // Insert transcribed text into message input
    setMessageText(prevText => {
      const currentText = prevText.trim();
      return currentText ? `${currentText}\n\n${transcript}` : transcript;
    });
    setSpeechToTextDialogOpen(false);
  };

  const handleSignatureSave = (signature: string, enabled: boolean) => {
    setUserSignature(signature);
    setSignatureEnabled(enabled);
    
    // Save to localStorage
    localStorage.setItem('userSignature', signature);
    localStorage.setItem('signatureEnabled', enabled.toString());
  };

  const handleParticipantsUpdated = () => {
    // Reload participants and conversations
    if (selectedConversation) {
      const loadParticipants = async () => {
        try {
          const response = await conversationsAPI.getParticipants(selectedConversation.id);
          setParticipants(response.participants || []);
        } catch (err) {
          console.error('Failed to reload participants:', err);
        }
      };
      loadParticipants();
    }
    handleAssignmentComplete(); // Also reload conversations
  };

  const handleAISuggestionSelect = (suggestion: string) => {
    setMessageText(prevText => {
      const currentText = prevText.trim();
      return currentText ? `${currentText}\n\n${suggestion}` : suggestion;
    });
  };

  const sendSelectedTemplate = async () => {
    if (!selectedConversation || !selectedTemplate) return false;
    try {
      const response = await api.post(`/whatsapp/templates/${selectedTemplate.id}/send`, {
        conversation_id: selectedConversation.id,
        variables: selectedTemplate.variables,
        channel_id: selectedConversation.channel?.id,
      });
      if (response.data?.success) {
        setSelectedTemplate(null);
        setMessageText('');
        loadMessages();
        setTemplateSuggestorRefreshKey(k => k + 1);
        return true;
      }
      throw new Error(response.data?.error || 'Failed to send template');
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Failed to send template';
      setError(detail);
      alert(detail);
      return false;
    }
  };

  const applyHandoffState = (conversationId: number, state: {
    ai_enabled: boolean;
    escalated: boolean;
    escalated_at?: string | null;
    escalated_by?: string | null;
    escalation_reason?: string | null;
    escalation_trigger?: string | null;
    assignee_id?: number | null;
  }) => {
    const patch = {
      ai_enabled: Boolean(state.ai_enabled),
      escalated: Boolean(state.escalated),
      escalated_at: state.escalated_at || undefined,
      escalated_by: state.escalated_by || undefined,
      escalation_reason: state.escalation_reason || undefined,
      escalation_trigger: state.escalation_trigger || undefined,
      assignee_id: state.assignee_id ?? undefined,
      assignee: state.assignee_id ? selectedConversation?.assignee : undefined,
    };
    setSelectedConversation(prev =>
      prev && prev.id === conversationId ? { ...prev, ...patch } : prev
    );
    setConversations(prevConversations =>
      prevConversations.map(conv =>
        conv.id === conversationId ? { ...conv, ...patch } : conv
      )
    );
  };

  const refreshSelectedConversation = async (conversationId: number) => {
    try {
      const fresh = await conversationsAPI.getConversation(conversationId);
      applyHandoffState(conversationId, fresh);
      setSelectedConversation(prev =>
        prev && prev.id === conversationId ? { ...prev, ...fresh } : prev
      );
      setConversations(prev =>
        prev.map(conv => (conv.id === conversationId ? { ...conv, ...fresh } : conv))
      );
    } catch (err) {
      console.error('Failed to refresh conversation:', err);
    }
  };

  // Sync dashboard when bot sends a handover message but UI still shows AI mode
  useEffect(() => {
    if (!selectedConversation?.id || !selectedConversation.ai_enabled || messages.length === 0) {
      return;
    }
    const lastOutgoing = [...messages].reverse().find(
      (m) => String(m.message_type).toLowerCase() === 'outgoing'
    );
    if (lastOutgoing?.content && isBotHandoverMessage(lastOutgoing.content)) {
      refreshSelectedConversation(selectedConversation.id);
    }
  }, [messages, selectedConversation?.id, selectedConversation?.ai_enabled]);

  // Handle AI toggle for current conversation
  const handleAiToggle = async () => {
    if (!selectedConversation) return;

    try {
      const target = !selectedConversation.ai_enabled;
      const response = await conversationsAPI.setAIEnabled(selectedConversation.id, target);
      applyHandoffState(selectedConversation.id, response);
      console.log(`🤖 AI ${response.ai_enabled ? 'enabled' : 'disabled'} for conversation #${selectedConversation.display_id}`);
    } catch (err) {
      console.error('Failed to toggle AI:', err);
      setError('Failed to toggle AI assistant');
    }
  };

  // Check if AI is enabled for current conversation
  const isAiEnabled = selectedConversation?.ai_enabled || false;

  // Handle AI auto-response for incoming messages
  const handleAiAutoResponse = async (conversationId: number, incomingMessage: Message) => {
    try {
      console.log('🤖 AI auto-response triggered for conversation:', conversationId);
      console.log('   Incoming message:', incomingMessage.content);

      // Call backend AI API to generate response
      const aiResponse = await captainAIAPI.generateAutoResponse(conversationId, {
        message: incomingMessage.content,
        message_id: incomingMessage.id
      });

      if (aiResponse && aiResponse.response) {
        // Post AI-generated response as outgoing message
        const response = await messagesAPI.sendMessage(conversationId, {
          content: aiResponse.response,
          message_type: 'outgoing',
          private: false
        });

        console.log('   AI response sent:', response.id);
        console.log('   Confidence:', aiResponse.confidence);
      } else {
        console.warn('   No AI response generated');
      }
    } catch (err) {
      console.error('❌ AI auto-response failed:', err);
      // Fail silently - don't disrupt user experience
    }
  };

  // Handle loading older messages (v1 smart pagination logic)
  const handleLoadOlderMessages = async () => {
    if (!selectedConversation || loadingOlderMessages || !hasMoreMessages) return;

    setLoadingOlderMessages(true);

    try {
      const currentlyLoaded = messages.length;
      const alreadySkipped = totalMessages - currentlyLoaded;

      // Load the previous 50 (or less if near the beginning)
      const toLoad = Math.min(50, alreadySkipped);
      const newSkip = alreadySkipped - toLoad;

      // Calculate which page this corresponds to
      const page = Math.floor(newSkip / 50) + 1;
      const skipWithinPage = newSkip % 50;

      const response = await messagesAPI.getMessages(selectedConversation.id, {
        page,
        per_page: 50
      });

      let olderMessages = response.messages || [];

      // Slice to get only the messages we need
      if (skipWithinPage > 0 || toLoad < 50) {
        const endIndex = skipWithinPage + toLoad;
        olderMessages = olderMessages.slice(skipWithinPage, endIndex);
      }

      if (olderMessages.length > 0) {
        setMessages(prev => [...olderMessages, ...prev]);
        setCurrentPage(page);
        setHasMoreMessages(newSkip > 0);
      }
    } catch (err) {
      console.error('Failed to load older messages:', err);
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  // commented out for timezone implementation - 08/02/2026
  // const formatMessageTime = (dateString: string) => {
  //   const date = new Date(dateString);
  //   const now = new Date();
  //   const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  //   const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
  //   if (messageDate.getTime() === today.getTime()) {
  //     // Today - show only time
  //     return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  //   } else if (messageDate.getTime() === today.getTime() - 24 * 60 * 60 * 1000) {
  //     // Yesterday
  //     return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  //   } else {
  //     // Older - show date and time
  //     return date.toLocaleDateString([], { 
  //       month: 'short', 
  //       day: 'numeric',
  //       year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  //     }) + ` ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  //   }
  // };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'success';
      case 'pending': return 'warning';
      case 'resolved': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ff5722';
      case 'high': return '#ff9800';
      case 'medium': return '#2196f3';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  // Old unicode icon implementation (commented out for reference)
  // const getChannelIcon = (type: string) => {
  //   switch (type) {
  //     case 'whatsapp': return '💬'; // WhatsApp
  //     case 'email': return '📧'; // Email
  //     case 'website': return '🌐'; // Website widget
  //     case 'api': return '🔗'; // API channel
  //     case 'facebook': return '📘'; // Facebook
  //     case 'twitter': return '🐦'; // Twitter/X
  //     case 'instagram': return '📷'; // Instagram
  //     case 'telegram': return '✈️'; // Telegram
  //     case 'sms': return '💬'; // SMS
  //     case 'slack': return '💼'; // Slack
  //     case 'custom_gpt': return '🤖'; // OpenAI Custom GPT
  //     case 'line': return '💚'; // LINE
  //     default: return '💬'; // Default chat bubble
  //   }
  // };

  // New SVG icon implementation
  const getChannelIcon = (type: string) => {
    const iconStyle = {
      width: '14px',
      height: '14px',
      display: 'inline-block',
      verticalAlign: 'middle'
    };

    const getIconPath = (channelType: string) => {
      switch (channelType) {
        case 'whatsapp': return '/icons/whatsapp.svg';
        case 'email': return '/icons/email.svg';
        case 'website': return '/icons/website.svg';
        case 'api': return '/icons/api.svg';
        case 'facebook': return '/icons/facebook.svg';
        case 'twitter': return '/icons/twitter.svg';
        case 'instagram': return '/icons/instagram.svg';
        case 'telegram': return '/icons/telegram.svg';
        case 'sms': return '/icons/sms.svg';
        case 'slack': return '/icons/slack.svg';
        case 'custom_gpt': return '/icons/custom_gpt.svg';
        case 'line': return '/icons/website.svg'; // Fallback to website icon for LINE
        default: return '/icons/api.svg'; // Default fallback
      }
    };

    return (
      <img
        src={getIconPath(type)}
        alt={`${type} channel`}
        style={iconStyle}
      />
    );
  };

  const hasPreChatFormData = (conversation: Conversation) => {
    // return conversation.contact.custom_attributes && 
    //        Object.keys(conversation.contact.custom_attributes).length > 0;
    const attrs = conversation.contact?.custom_attributes;
    return !!attrs && Object.keys(attrs).length > 0;
  };

  const formatPreChatFormData = (customAttributes: Record<string, any>) => {
    if (!customAttributes) return [];
    
    return Object.entries(customAttributes)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
        value: typeof value === 'object' ? JSON.stringify(value) : String(value)
      }));
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Media base URL for attachments (works on server: use REACT_APP_API_URL without /api/v1)
  const getMediaBaseUrl = () => {
    const api = process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1';
    const base = api.replace(/\/api\/v1\/?$/, '') || api.split('/api')[0];
    return base || 'http://localhost:8001';
  };

  const renderAttachment = (message: Message) => {

    const mediaBase = getMediaBaseUrl();

    // Check for new attachments array first (v2 system)
    if (message.attachments && message.attachments.length > 0) {
      return (
        <Box sx={{ mt: 1 }}>
          {message.attachments.map((attachment) => {
            const { file_type, file_url, file_size, mime_type } = attachment;
            const filename = file_url.split('/').pop() || 'file';

            // For images, show preview
            if (file_type === 'image') {
              return (
                <Box key={attachment.id} sx={{ mb: 1 }}>
                  <img
                    //src={`http://localhost:8001${file_url}`}
                    src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`}
                    alt={filename}
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                    //onClick={() => window.open(`http://localhost:8001${file_url}`, '_blank')}
                    onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`, '_blank')}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                    {filename} ({formatFileSize(file_size)})
                  </Typography>
                </Box>
              );
            }

            // For audio files, show audio player
            if (file_type === 'audio') {
              return (
                <Box key={attachment.id} sx={{ mb: 1 }}>
                  <audio
                    controls
                    style={{ width: '250px' }}
                    //src={`http://localhost:8001${file_url}`}
                    src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                    🎵 {filename} ({formatFileSize(file_size)})
                  </Typography>
                </Box>
              );
            }

            // For video files, show video player
            if (file_type === 'video') {
              return (
                <Box key={attachment.id} sx={{ mb: 1 }}>
                  <video
                    controls
                    style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px' }}
                    //src={`http://localhost:8001${file_url}`}
                    src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                    🎥 {filename} ({formatFileSize(file_size)})
                  </Typography>
                </Box>
              );
            }

            // For other files (documents, etc.), show download link
            const getFileIcon = (type?: string) => {
              switch (type) {
                case 'document': return '📄';
                case 'audio': return '🎵';
                case 'video': return '🎥';
                case 'image': return '🖼️';
                default: return '📎';
              }
            };

            return (
              <Box key={attachment.id} sx={{ mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<span>{getFileIcon(file_type)}</span>}
                  //onClick={() => window.open(`http://localhost:8001${file_url}`, '_blank')}
                  onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`, '_blank')}
                  sx={{ textTransform: 'none' }}
                >
                  {filename}
                </Button>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                  {formatFileSize(file_size)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
	
    // WhatsApp audio without attachments yet (e.g. still downloading): show placeholder
    const isAudioPlaceholder = (message.content === '[Audio Message]' || message.content === '[audio message]') &&
      message.content_type === 'audio';
    if (isAudioPlaceholder) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            🎵 Audio message
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
            (loading or unavailable)
          </Typography>
        </Box>
      );
    }
	
    // Fallback to old content_attributes system (backward compatibility)
    if (!message.content_attributes?.file_url || message.content_type === 'text') {
      return null;
    }

    const { filename, file_size, file_type, file_url, mime_type } = message.content_attributes;
    
    // For images, show preview
    if (message.content_type === 'image') {
      return (
        <Box sx={{ mt: 1 }}>
          <img 
            //src={`http://localhost:8001${file_url}`}
            src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`}
            alt={filename}
            style={{ 
              maxWidth: '200px', 
              maxHeight: '200px', 
              borderRadius: '8px',
              cursor: 'pointer'
            }}
            //onClick={() => window.open(`http://localhost:8001${file_url}`, '_blank')}
            onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`, '_blank')}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
            {filename} ({formatFileSize(file_size)})
          </Typography>
        </Box>
      );
    }

    // For audio files, show audio player
    if (message.content_type === 'audio') {
      return (
        <Box sx={{ mt: 1 }}>
          <audio 
            controls 
            style={{ width: '250px' }}
            //src={`http://localhost:8001${file_url}`}
            src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
            🎵 {filename} ({formatFileSize(file_size)})
          </Typography>
        </Box>
      );
    }

    // For video files, show video player
    if (message.content_type === 'video') {
      return (
        <Box sx={{ mt: 1 }}>
          <video 
            controls 
            style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '8px' }}
            //src={`http://localhost:8001${file_url}`}
            src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
            🎥 {filename} ({formatFileSize(file_size)})
          </Typography>
        </Box>
      );
    }

    // For other files, show download link
    const getFileIcon = (type?: string) => {
      switch (type) {
        case 'document': return '📄';
        case 'audio': return '🎵';
        case 'video': return '🎥';
        default: return '📎';
      }
    };

    return (
      <Box sx={{ mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<span>{getFileIcon(file_type)}</span>}
          //onClick={() => window.open(`http://localhost:8001${file_url}`, '_blank')}
          onClick={() => window.open(`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}${file_url}`, '_blank')}
          sx={{ textTransform: 'none' }}
        >
          {filename}
        </Button>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
          {formatFileSize(file_size)}
        </Typography>
      </Box>
    );
  };

  // Render button response message
  const renderButtonResponse = (message: Message) => {
    const metadata = message.metadata || {};
    const buttonData = metadata.button || metadata.interactive;
    
    if (buttonData) {
      const buttonText = buttonData.text || buttonData.title || buttonData.payload || "Button clicked";
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            icon={<AssignmentIcon />} 
            label="Button Response" 
            size="small" 
            color="primary" 
            variant="outlined"
          />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {buttonText}
          </Typography>
        </Box>
      );
    }
    return null;
  };
  // Fix 3: Performance optimization - Memoize filtered and sorted conversations
  // This prevents unnecessary re-sorting on every render
  const filteredConversations = useMemo(() => {
    return conversations.filter(conv => {
      // const matchesSearch = conv.contact.name.toLowerCase().includes(searchText.toLowerCase()) ||
      //                      conv.contact.email?.toLowerCase().includes(searchText.toLowerCase());      
      const needle = searchText.trim().toLowerCase();
      const matchesSearch =
        !needle ||
        (() => {
          const c = conv.contact;
          if (!c) return false;
          const label = contactDisplayLabel(c).toLowerCase();
          const email = (c.email ?? '').toLowerCase();
          return label.includes(needle) || email.includes(needle);
        })();
      const matchesStatus = filterStatus === 'all' || conv.status === filterStatus;
      return matchesSearch && matchesStatus;
    }).sort((a, b) => {
      let result = 0;

      switch (sortBy) {
        case 'last_activity_at':
          result = new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
          break;
        case 'id':  // ADD THIS NEW CASE added on 27/01/2026
          result = (b.id || 0) - (a.id || 0);  // Sort by conversation ID (latest first)
          break;  
        case 'created_at':
          // Handle undefined/null created_at gracefully
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
          result = bTime - aTime;
          break;
        case 'contact_name':
          // result = a.contact.name.localeCompare(b.contact.name);          
          result = contactDisplayLabel(a.contact).localeCompare(contactDisplayLabel(b.contact));
          break;
        case 'status':
          result = a.status.localeCompare(b.status);
          break;
        case 'priority':
          const priorityOrder: Record<string, number> = {
            'urgent': 0,
            'high': 1,
            'medium': 2,
            'low': 3
          };
          const aPriority = priorityOrder[a.priority] ?? 999;
          const bPriority = priorityOrder[b.priority] ?? 999;
          result = aPriority - bPriority;
          break;
        default:
          result = 0;
      }
      // Secondary sort by last_activity_at if primary sort is equal
      if (result === 0 && sortBy !== 'last_activity_at') {
        result = new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
      }

      return result;
    });
  }, [conversations, searchText, filterStatus, sortBy]);

/* 
  }).sort((a, b) => {
    switch (sortBy) {
      case 'last_activity_at':
        return new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
      case 'created_at':
        return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
      case 'contact_name':
        return a.contact.name.localeCompare(b.contact.name);
      case 'status':
        return a.status.localeCompare(b.status);
      case 'priority':
        const priorityOrder = { 'urgent': 0, 'high': 1, 'medium': 2, 'low': 3 };
        return (priorityOrder[a.priority as keyof typeof priorityOrder] || 4) - 
               (priorityOrder[b.priority as keyof typeof priorityOrder] || 4);
      default:
        return 0;
    }
  });
*/

  if (error) {
    return (
      <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex' }}>
      {/* Conversations List */}
      <Paper sx={{ 
        width: sidebarCollapsed ? 60 : 400, 
        display: 'flex', 
        flexDirection: 'column', 
        mr: 2,
        transition: 'width 0.3s ease'
      }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {!sidebarCollapsed && 'Conversations'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {!sidebarCollapsed && (
                <>
                  <Tooltip title="Download (this conversation or all)">
                    <IconButton
                      size="small"
                      onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                      disabled={exporting}
                    >
                      {exporting ? <CircularProgress size={18} /> : <Download fontSize="small" />}
                    </IconButton>
                  </Tooltip>				
                  <Tooltip title="Sort">
                    <IconButton 
                      size="small"
                      onClick={(e) => setSortAnchorEl(e.currentTarget)}
                    >
                      <Sort fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={() => setSortAnchorEl(null)}
                  > 
                    <MenuItem onClick={() => { setSortBy('id'); setSortAnchorEl(null); }}>
                    Conversation ID
                    </MenuItem>
                    <MenuItem onClick={() => { setSortBy('last_activity_at'); setSortAnchorEl(null); }}>
                      Last Activity
                    </MenuItem>
                    <MenuItem onClick={() => { setSortBy('created_at'); setSortAnchorEl(null); }}>
                      Created Date
                    </MenuItem>
                    <MenuItem onClick={() => { setSortBy('contact_name'); setSortAnchorEl(null); }}>
                      Contact Name
                    </MenuItem>
                    <MenuItem onClick={() => { setSortBy('status'); setSortAnchorEl(null); }}>
                      Status
                    </MenuItem>
                    <MenuItem onClick={() => { setSortBy('priority'); setSortAnchorEl(null); }}>
                      Priority
                    </MenuItem>
                  </Menu>
                </>
              )}
              <Tooltip title={sidebarCollapsed ? "Expand" : "Collapse"}>
                <IconButton 
                  size="small"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                  {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {!sidebarCollapsed && (
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 48,
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': { opacity: 0.3 },
                },
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  px: 2,
                }
              }}
            >
              {availableTabs.map((tab, index) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: selectedTab === index ? 600 : 400,
                  }}
                />
              ))}
            </Tabs>
          )}
        </Box>

        {/* Filters */}
        {!sidebarCollapsed && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="snoozed">Snoozed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Conversations List */}
        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {!loading && filteredConversations.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                No conversations found for your account.
              </Typography>
              <Typography variant="caption">
                WhatsApp traffic is on RentRemote account 2 — sign in with an account 2 user
                (e.g. agent@sample.com) to see UAT WhatsApp threads.
              </Typography>
            </Box>
          )}
          {filteredConversations.map((conversation) => (
            <ListItem
              key={conversation.id}
              button
              selected={selectedConversation?.id === conversation.id}
              onClick={() => setSelectedConversation(conversation)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                },
              }}
            >
              <ListItemAvatar>
                <Badge badgeContent={conversation.unread_count} color="error">
                  <Avatar>
                  {/* {conversation.contact.name.charAt(0)} */}
                    {(contactDisplayLabel(conversation.contact) || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                    {/* {conversation.contact.name} */}
                      {contactDisplayLabel(conversation.contact) || 'Unknown contact'}                      
                    </Typography>
                    <span style={{ fontSize: '14px' }}>
                      {getChannelIcon(conversation.channel.channel_type)}
                    </span>
                    {hasPreChatFormData(conversation) && (
                      <Tooltip title="Has pre-chat form data">
                        <ContactPage sx={{ fontSize: 12, color: '#1976d2' }} />
                      </Tooltip>
                    )}
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: getPriorityColor(conversation.priority),
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={conversation.status}
                        size="small"
                        color={getStatusColor(conversation.status) as any}
                        variant="outlined"
                      />
                      {conversation.labels.map((label) => (
                        <Chip
                          key={label.id}
                          label={label.title}
                          size="small"
                          sx={{ bgcolor: label.color, color: 'white', fontSize: '10px' }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      #{conversation.display_id} • {/*{new Date(conversation.last_activity_at).toLocaleTimeString()}*/ formatMessageTime(conversation.last_activity_at)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Conversation Details */}
      {selectedConversation ? (
        <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
          {/* Main Conversation Panel */}
          <Paper sx={{ flex: copilotEnabled ? '1 1 60%' : 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Conversation Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Avatar sx={{ mr: 1 }}>
              {/* {selectedConversation.contact.name.charAt(0)}                 */}
                {(contactDisplayLabel(selectedConversation.contact) || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                <Typography variant="h6" noWrap>
                {/* {selectedConversation.contact.name}                   */}
                  {contactDisplayLabel(selectedConversation.contact) || 'Unknown contact'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                {/* {selectedConversation.contact.email} • #{selectedConversation.display_id}                   */}
                  {selectedConversation.contact?.email ?? '—'} • #{selectedConversation.display_id}
                </Typography>
              </Box>
              {/* Co-Pilot Toggle */}
              <CoPilotToggle
                enabled={copilotEnabled}
                onChange={setCopilotEnabled}
              />
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Tooltip title="Assign">
                <Button
                  size="small"
                  startIcon={<Person />}
                  onClick={() => setAssignmentDialogOpen(true)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  {selectedConversation.assignee ? selectedConversation.assignee.first_name : 'Assign'}
                </Button>
              </Tooltip>
              <Tooltip title="Labels">
                <IconButton
                  size="small"
                  onClick={() => setLabelsDialogOpen(true)}
                >
                  <Label fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Participants">
                <IconButton
                  size="small"
                  onClick={() => setParticipantsDialogOpen(true)}
                >
                  <Group fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={selectedConversation.status === 'resolved' ? 'Resolved' : 'Resolve'}>
                <IconButton
                  size="small"
                  onClick={() => handleStatusChange('resolved')}
                  color={selectedConversation.status === 'resolved' ? 'success' : 'default'}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Analytics">
                <IconButton
                  size="small"
                  onClick={() => setMetricsDialogOpen(true)}
                >
                  <Analytics fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download conversation">
                <IconButton
                  size="small"
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  disabled={exporting}
                >
                  {exporting ? <CircularProgress size={20} /> : <Download fontSize="small" />}
                </IconButton>
              </Tooltip>			  
              {/* AI Conversation Controls */}
              <AIConversationControls
                conversationId={selectedConversation.id}
                aiEnabled={isAiEnabled}
                escalated={selectedConversation.escalated || false}
                onHandoffChange={(state) => {
                  applyHandoffState(selectedConversation.id, state);
                }}
              />
              {hasPreChatFormData(selectedConversation) && (
                <Tooltip title="Pre-chat Info">
                  <IconButton
                    size="small"
                    onClick={() => setShowPreChatData(!showPreChatData)}
                    color={showPreChatData ? 'primary' : 'default'}
                  >
                    <ContactPage fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* AI Status Indicator */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#f9f9f9' }}>
            <AIStatusIndicator
              aiEnabled={isAiEnabled}
              escalated={selectedConversation.escalated}
              escalationReason={selectedConversation.escalation_reason}
              isProcessing={false}
              showDetails={true}
            />
          </Box>

          {/* Escalation Banner */}
          {selectedConversation.escalated && !isAiEnabled && (
            <Box sx={{ p: 2, pt: 0 }}>
              <EscalationBanner
                conversationId={selectedConversation.id}
                escalated={selectedConversation.escalated}
                escalationReason={selectedConversation.escalation_reason}
                escalationTrigger={selectedConversation.escalation_trigger}
                escalatedAt={selectedConversation.escalated_at ? new Date(selectedConversation.escalated_at) : undefined}
                escalatedBy={selectedConversation.escalated_by}
                assignedAgent={selectedConversation.assignee}
                onHandoffChange={(state) => {
                  applyHandoffState(selectedConversation.id, state);
                }}
              />
            </Box>
          )}

          {/* Pre-chat Form Data */}
          {hasPreChatFormData(selectedConversation) && (
            <Collapse in={showPreChatData}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
                <Card elevation={0} sx={{ backgroundColor: 'transparent' }}>
                  <CardHeader
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ContactPage color="primary" />
                        <Typography variant="h6">Pre-chat Form Information</Typography>
                      </Box>
                    }
                    sx={{ pb: 1 }}
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                    {/* {formatPreChatFormData(selectedConversation.contact.custom_attributes || {}).map((item) => (                       */}
                      {formatPreChatFormData(selectedConversation.contact?.custom_attributes || {}).map((item) => (
                        <Box key={item.key}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            {item.label}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Collapse>
          )}

          {/* Messages */}
          <Box
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            sx={{ flex: 1, overflow: 'auto', p: 2 }}
          >
            {/* Loading indicator for older messages */}
            {loadingOlderMessages && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {messages.map((message) => {
              // Use AI Message Bubble for AI-generated messages
              if (message.sender_type === 'ai' || message.sender_type === 'bot') {
                return (
                  <AIMessageBubble
                    key={message.id}
                    content={message.content}
                    confidence={message.ai_confidence}
                    toolsUsed={message.tools_used}
                    responseTimeMs={message.response_time_ms}
                    isStreaming={message.streaming || false}
                    timestamp={new Date(message.created_at)}
                    showMetadata={true}
                  />
                );
              }

              // Regular message rendering for human messages
              return (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: message.message_type === 'outgoing' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor:
                        message.message_type === 'outgoing' ? '#1f93ff' :
                        message.private ? '#fff3e0' : '#f5f5f5',
                      color: message.message_type === 'outgoing' ? 'white' : 'black',
                    }}
                  >
                    {message.private && (
                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', mb: 0.5 }}>
                        Private Note
                      </Typography>
                    )}
                    {/* Show button response if metadata exists, otherwise show regular content */}
                    {message.metadata?.button || message.metadata?.interactive ? (
                      renderButtonResponse(message)
                    ) : (
                    <MarkdownRenderer
                      // content={message.content}

                      content={
                        // Don't show "[Audio Message]" when we render audio player or placeholder
                        (message.content === '[Audio Message]' || message.content === '[audio message]') &&
                        (message.content_type === 'audio' || (message.attachments?.some((a: { file_type?: string }) => a.file_type === 'audio')))
                          ? ''
                          : message.content
                      }
					  
                      sx={{
                        fontSize: '0.875rem',
                        color: message.message_type === 'outgoing' ? 'white' : 'black',
                        '& a': { color: message.message_type === 'outgoing' ? 'white' : '#1976d2' },
                        '& code': {
                          backgroundColor: message.message_type === 'outgoing'
                            ? 'rgba(255, 255, 255, 0.2)'
                            : 'rgba(0, 0, 0, 0.05)'
                        },
                        '& pre': {
                          backgroundColor: message.message_type === 'outgoing'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    />
                    )}
                    {renderAttachment(message)}
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                      {formatMessageTime(message.created_at)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {/* Auto-scroll anchor */}
            <div ref={messagesEndRef} />
          </Box>

          {/* Phase 5: WhatsApp Template Suggestor (2026-01-19) */}
          {selectedConversation?.channel?.channel_type === 'whatsapp' && (
            <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
              <TemplateSuggestor
                conversationId={selectedConversation.id}
                contactPhone={selectedConversation.contact?.phone_number}
                // contact={selectedConversation?.contact}
                contact={
                  selectedConversation.contact
                    ? {
                        ...selectedConversation.contact,
                        name: selectedConversation.contact.name ?? undefined,
                      }
                    : undefined
                }
                refreshTrigger={templateSuggestorRefreshKey}
                onSelectTemplate={(template, variables) => {
                  setSelectedTemplate({
                    id: template.id,
                    name: template.name,
                    variables: variables
                  });
                  const bodyText = template.components?.body;
                  if (bodyText) {
                    let preview = bodyText;
                    Object.entries(variables).forEach(([key, value]) => {
                      preview = preview.replace(`{{${key}}}`, value);
                    });
                    setMessageText(preview);
                  } else {
                    setMessageText(`[Template: ${template.name}]`);
                  }
                }}
                onSendTemplate={async () => {
                  await sendSelectedTemplate();
                }}
                compact
              />
            </Box>
          )}

          {/* Phase 6: Email Template Suggestor (2026-02-09) */}
          {selectedConversation?.channel?.channel_type === 'email' && (
              <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
                <EmailTemplateSuggestor
                  conversationId={selectedConversation.id}
                  onSent={() => {
                    // Refresh messages after sending email template
                    loadMessages();
                  }}
                />
              </Box>
            )}
			
          {/* Message Input */}
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <RichTextEditor
              value={messageText}
              // onChange={setMessageText}
              onChange={handleMessageTextChange}
              onSend={handleSendMessage}
              onAttachFile={handleFileAttachment}
              onStartRecording={() => setVoiceRecorderOpen(true)}
              onStartSpeechToText={() => setSpeechToTextDialogOpen(true)}
              onOpenSignatureDialog={() => setSignatureDialogOpen(true)}
              placeholder={
                selectedTemplate
                  ? 'Template selected — click Send or use Send Template above'
                  : 'Type your message...'
              }
              disabled={uploading}
              showSignatureButton={true}
            />
          </Box>
        </Paper>

        {/* Co-Pilot Panel */}
        {copilotEnabled && (
          <Box sx={{ flex: '0 0 40%', minWidth: 400 }}>
            <CoPilotPanel conversationId={selectedConversation.id} />
          </Box>
        )}
      </Box>
      ) : (
        <Paper sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Select a conversation to start messaging
          </Typography>
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleStatusChange('snoozed')}>
          <Schedule sx={{ mr: 1 }} /> Snooze
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('pending')}>
          <RadioButtonUnchecked sx={{ mr: 1 }} /> Mark as pending
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('open')}>
          <RadioButtonUnchecked sx={{ mr: 1 }} /> Reopen
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('resolved')}>
          <CheckCircle sx={{ mr: 1 }} /> Resolve
        </MenuItem>
      </Menu>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>This conversation</MenuItem>
        <MenuItem onClick={() => handleDownloadCurrent('csv')} disabled={!selectedConversation || exporting}>CSV</MenuItem>
        <MenuItem onClick={() => handleDownloadCurrent('excel')} disabled={!selectedConversation || exporting}>Excel</MenuItem>
        <MenuItem onClick={() => handleDownloadCurrent('json')} disabled={!selectedConversation || exporting}>JSON</MenuItem>
        <Divider />
        <MenuItem disabled sx={{ opacity: 1, fontWeight: 600 }}>All conversations (current list)</MenuItem>
        <MenuItem onClick={() => handleDownloadAll('csv')} disabled={exporting}>CSV</MenuItem>
        <MenuItem onClick={() => handleDownloadAll('excel')} disabled={exporting}>Excel</MenuItem>
        <MenuItem onClick={() => handleDownloadAll('json')} disabled={exporting}>JSON</MenuItem>
      </Menu>

      {/* Assignment Dialog */}
      <AssignmentDialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentAssignee={selectedConversation?.assignee}
        onAssigned={handleAssignmentComplete}
      />

      {/* Labels Dialog */}
      <LabelsDialog
        open={labelsDialogOpen}
        onClose={() => setLabelsDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentLabels={selectedConversation?.labels || []}
        onLabelsUpdated={handleLabelsUpdated}
      />

      {/* Voice Recorder */}
      <VoiceRecorder
        open={voiceRecorderOpen}
        onClose={() => setVoiceRecorderOpen(false)}
        onSendRecording={handleVoiceRecording}
      />

      {/* Speech-to-Text Dialog */}
      <SpeechToTextDialog
        open={speechToTextDialogOpen}
        onClose={() => setSpeechToTextDialogOpen(false)}
        onTranscriptComplete={handleSpeechToTextComplete}
      />

      {/* Signature Dialog */}
      <SignatureDialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSaveSignature={handleSignatureSave}
        currentSignature={userSignature}
        signatureEnabled={signatureEnabled}
      />

      {/* Participants Dialog */}
      <ParticipantsDialog
        open={participantsDialogOpen}
        onClose={() => setParticipantsDialogOpen(false)}
        conversationId={selectedConversation?.id || 0}
        currentParticipants={participants}
        onParticipantsUpdated={handleParticipantsUpdated}
      />

      {/* Conversation Metrics */}
      {selectedConversation && (
        <ConversationMetrics
          open={metricsDialogOpen}
          onClose={() => setMetricsDialogOpen(false)}
          conversationId={selectedConversation.id}
          conversation={selectedConversation}
        />
      )}

      {/* Phase 5: WhatsApp Conversation Initiator (2026-01-19) */}
      <ConversationInitiator
        open={conversationInitiatorOpen}
        onClose={() => setConversationInitiatorOpen(false)}
        onConversationCreated={(newConversationId) => {
          // Refresh conversations and select the new one
          loadConversations();
          // Navigate to the new conversation
          window.location.href = `/conversations/${newConversationId}`;
        }}
      />
    </Box>
  );
};

export default ConversationsPage;